// frontend/src/App.js
// Main application container with Redux authentication and role-based routing
// This is the main React app shell, loading the current user and wiring up role-based routing (UPS vs Agency), protected dashboards, auth pages, and the shared header/layout.

import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// socket bootstrap import
import { initSocket } from './services/socket';

// Global components
import Header from './components/Header';

// Auth pages
import Login from './features/auth/Login';
import ForgotPassword from './features/auth/ForgotPassword';
import ResetPassword from './features/auth/ResetPassword';

// UPS admin layout and pages
import UpsDashboard from './features/ups/UpsDashboard';
import OrganizedDriverList from './features/ups/OrganizedDriverList';
import UpsReservedDriverList from './features/ups/UpsReservedDriverList';
import UpsRequestsDriverList from './features/ups/UpsRequestsDriverList';
import BannedDriversList from './features/ups/BannedDriversList';
import UpsBlockedAssignmentsList from './features/ups/UpsBlockedAssignmentsList';
import AccountDashboard from './features/ups/AccountDashboard';
import ManageAgencies from './features/ups/ManageAgencies';
import ManageSites from './features/ups/ManageSites';


// site loading
import {
  loadMyCompanySites,
  loadMyAgencyAllowedSites,
} from './features/sites/sitesSlice';

// Agency layout and pages
import AgencyDashboard from './features/agency/Dashboard';
import ActiveDriverList from './features/agency/ActiveDriverList';
import ReservedDriverList from './features/agency/ReservedDriverList';
import AvailableDriverList from './features/agency/AvailableDriverList';
import BannedDriverList from './features/agency/BannedDriverList';

// Auth actions
import { fetchCurrentUser } from './features/auth/authSlice';

// Layout for public/default routes
function DefaultLayout() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

function App() {
  const dispatch = useDispatch();
  const { userInfo, loading } = useSelector(state => state.auth);

  // 1) restore session
  useEffect(() => {
    if (localStorage.getItem('accessToken')) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch]);

  // 2) init socket after auth is known
  useEffect(() => {
    if (!userInfo) return;
    // ✅ connect socket only once auth is known
    initSocket();
  }, [userInfo]);

  // 3) role-aware site loading (MUST be before any early returns)
  useEffect(() => {
    if (!userInfo) return;

    const role = userInfo.role;

    if (role === 'agency_admin' || role === 'agency_user') {
      dispatch(loadMyAgencyAllowedSites());
    } else if (role === 'ups_admin' || role === 'ups_user') {
      dispatch(loadMyCompanySites());
    }
  }, [dispatch, userInfo]);

  // ✅ now early returns are safe
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  const isLoggedIn = Boolean(userInfo);
  const role = userInfo?.role;

  return (
    <Router>
      {/* Show global header only if logged in */}
      {isLoggedIn && <Header />}

      <Routes>
        {/* UPS Admin Dashboard and Routes */}
        <Route
          path="/ups-admin/*"
          element={
            isLoggedIn && ['ups_admin', 'ups_user'].includes(role)
              ? <UpsDashboard />
              : <Navigate to="/" replace />
          }
        >
          {/* Group all driver-list routes under /drivers */}
          <Route path="drivers/">
            {/* GET /ups-admin/drivers */}
            <Route index           element={<OrganizedDriverList   />} />
            {/* GET /ups-admin/drivers/active */}
            <Route path="active"   element={<OrganizedDriverList   />} />
            {/* GET /ups-admin/drivers/requests */}
            <Route path="requests" element={<UpsRequestsDriverList />} />
            {/* GET /ups-admin/drivers/reserved */}
            <Route path="reserved" element={<UpsReservedDriverList />} />
            {/* GET /ups-admin/drivers/banned */}
            <Route path="banned"   element={<BannedDriversList      />} />
            <Route path="blocked"  element={<UpsBlockedAssignmentsList />} />
            {/* Other UPS pages */}
          </Route>

          {/* Other UPS pages */}
          <Route path="accounts"        element={<AccountDashboard />} />
          <Route path="manage-agencies" element={<ManageAgencies />} />
          <Route path="manage-sites" element={<ManageSites />} />
          <Route path="*" element={<Navigate to="/ups-admin/drivers" replace />} />
        </Route>

        {/* Agency Dashboard and Routes */}
        <Route
          path="/agency/*"
          element={
            isLoggedIn && role?.startsWith('agency')
              ? <AgencyDashboard />
              : <Navigate to="/" replace />
          }
        >
          {/* Default agency landing page */}
          <Route index element={<Navigate to="drivers" replace />} />

          <Route path="drivers" element={<ActiveDriverList />} />
          <Route path="reserved" element={<ReservedDriverList />} />
          <Route path="available" element={<AvailableDriverList />} />
          <Route path="banned" element={<BannedDriverList />} />
          <Route path="*" element={<Navigate to="drivers" replace />} />
        </Route>

        {/* Public / Auth Routes */}
        <Route element={<DefaultLayout />}>
          <Route
            path="/login"
            element={!isLoggedIn ? <Login /> : <Navigate to="/" replace />}
          />
          <Route
            path="/forgot-password"
            element={!isLoggedIn ? <ForgotPassword /> : <Navigate to="/" replace />}
          />
          <Route
            path="/reset-password"
            element={!isLoggedIn ? <ResetPassword /> : <Navigate to="/" replace />}
          />

          {/* Root path: Redirect based on role */}
          <Route
            path="/"
            element={
              isLoggedIn
                ? (role?.startsWith('agency')
                    ? <Navigate to="/agency/drivers" replace />
                    : <Navigate to="/ups-admin/drivers" replace />)
                : <Navigate to="/login" replace />
            }
          />

          {/* Catch-all fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
